import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { KindredMark } from "./KindredMark";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState(token ? "idle" : "no-token");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setState("error");
      setError("Passwords don't match.");
      return;
    }
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setState("done");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setState("error");
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <KindredMark className="mx-auto mb-4 h-14 w-14" />
          <h1 className="font-serif text-3xl tracking-tight text-cream">Choose a new password</h1>
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.25em] text-gold">Admin access</p>
        </div>

        {state === "no-token" && (
          <p className="rounded-3xl border border-cream/10 px-6 py-8 text-center text-sm leading-relaxed text-cream/60" data-testid="reset-no-token">
            This reset link is missing its token. Request a fresh one from the{" "}
            <Link to="/admin" className="text-gold underline">admin sign-in page</Link>.
          </p>
        )}

        {state === "done" && (
          <div className="text-center" data-testid="reset-success">
            <p className="rounded-3xl border border-gold/40 px-6 py-8 text-sm leading-relaxed text-cream/80">
              Your password is updated. You can sign in with it right away.
            </p>
            <Link
              to="/admin"
              data-testid="reset-goto-login"
              className="mt-6 inline-block rounded-full bg-gold px-8 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep"
            >
              Sign in
            </Link>
          </div>
        )}

        {(state === "idle" || state === "loading" || state === "error") && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <Input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (8+ characters)"
              aria-label="New password"
              data-testid="reset-password-input"
              className="h-12 rounded-full border-cream/20 bg-cream/5 px-5 text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold"
            />
            <Input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              data-testid="reset-confirm-input"
              className="h-12 rounded-full border-cream/20 bg-cream/5 px-5 text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold"
            />
            {state === "error" && (
              <p className="text-sm font-medium text-red-400" data-testid="reset-error">{error}</p>
            )}
            <Button
              type="submit"
              disabled={state === "loading"}
              data-testid="reset-submit-button"
              className="h-12 w-full rounded-full bg-gold text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95"
            >
              {state === "loading" ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
